import type { Schema } from 'firebase/ai';

// Built lazily, via a dynamic `import('firebase/ai')`, for the repository response schema
// itself -- so building this schema never forces the AI SDK into a bundle that hasn't asked for it.
//
// `optionalProperties` mirrors the domain model 1:1: every field the vet's note might not mention
// is listed there, so Gemini can omit the key entirely instead of inventing a value to fill it --
// `ClinicalReportMapper` is what turns "key absent" into the model's `null`/`[]` defaults.
export async function buildClinicalReportSchema(): Promise<Schema> {
  const { Schema } = await import('firebase/ai');

  const citedString = Schema.string();

  return Schema.object({
    properties: {
      resena: Schema.string(),
      motivoConsulta: Schema.string(),
      anamnesis: Schema.object({
        properties: {
          vacunacion: Schema.string(),
          desparasitacion: Schema.string(),
          dieta: Schema.string(),
          entorno: Schema.string(),
          tratamientoActual: Schema.string(),
        },
        optionalProperties: ['vacunacion', 'desparasitacion', 'dieta', 'entorno', 'tratamientoActual'],
      }),
      antecedentes: Schema.array({
        items: Schema.object({
          properties: { descripcion: Schema.string(), fecha: Schema.string() },
          optionalProperties: ['fecha'],
        }),
      }),
      enfermedadActual: Schema.array({
        items: Schema.object({
          properties: { fecha: Schema.string(), descripcion: Schema.string() },
          optionalProperties: ['fecha'],
        }),
      }),
      exploracionYPruebas: Schema.array({
        items: Schema.object({
          properties: {
            tipo: Schema.string(),
            fecha: Schema.string(),
            hallazgos: Schema.string(),
            conclusion: Schema.string(),
            citaTextual: citedString,
          },
          optionalProperties: ['fecha', 'conclusion', 'citaTextual'],
        }),
      }),
      diagnosticos: Schema.array({
        items: Schema.object({
          properties: {
            nombre: Schema.string(),
            categoria: Schema.enumString({ enum: ['principal', 'secundario'] }),
            tipo: Schema.enumString({ enum: ['diferencial', 'definitivo'] }),
            evidencia: Schema.string(),
            citaTextual: citedString,
          },
          optionalProperties: ['citaTextual'],
        }),
      }),
      plan: Schema.object({
        properties: {
          tratamientos: Schema.array({
            items: Schema.object({
              properties: {
                farmaco: Schema.string(),
                dosis: Schema.string(),
                via: Schema.string(),
                notas: Schema.string(),
                citaTextual: citedString,
              },
              optionalProperties: ['dosis', 'via', 'notas', 'citaTextual'],
            }),
          }),
          pruebasPendientes: Schema.array({ items: Schema.string() }),
          manejo: Schema.array({ items: Schema.string() }),
        },
        optionalProperties: ['tratamientos', 'pruebasPendientes', 'manejo'],
      }),
      pronostico: Schema.string(),
      recomendacionesPropietario: Schema.string(),
      datosNoEncontrados: Schema.array({ items: Schema.string() }),
      inconsistencias: Schema.array({
        items: Schema.object({
          properties: {
            descripcion: Schema.string(),
            fragmentos: Schema.array({ items: Schema.string() }),
          },
          optionalProperties: ['fragmentos'],
        }),
      }),
    },
    optionalProperties: [
      'resena', 'motivoConsulta', 'anamnesis', 'antecedentes', 'enfermedadActual',
      'exploracionYPruebas', 'diagnosticos', 'plan', 'pronostico', 'recomendacionesPropietario',
      'datosNoEncontrados', 'inconsistencias',
    ],
  });
}
